import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Icon,
  IconButton,
  Modal,
  TextField,
  toasterService
} from '@hevoio/hevo-ui-toolkit';
import { HdDropDown } from '../../../components';
import { IWorkspace } from '../Workspace/interfaces/workspace';
import { Role } from '../models';
import { WorkspacesAPI } from '../Workspace/api/WorkspacesAPI';
import { getErrorMessageFromObj } from '../../../core/utils/request';
import styles from './InviteWorkspaceUsersModal.module.scss';
import { IconUserPlus } from '@tabler/icons-react';
import { VALID_EMAIL_REGEX } from '../../../core/constants/constants';
import { optionsTooltipResolver } from './models';
import { useRolesResolver } from './useRolesResolver';

interface Props {
  open: boolean;
  onClose: () => void;
  onInviteSuccess: (emailCount: number) => void;
  workspaces: IWorkspace[];
  roles: Role[];
}

export function InviteWorkspaceUsersModal({
  open,
  onClose,
  onInviteSuccess,
  workspaces,
  roles
}: Props) {
  const [emails, setEmails] = useState('');
  const [selectedWorkspace, setSelectedWorkspace] = useState<IWorkspace | null>(null);
  const [selectedRoles, setSelectedRoles] = useState<Role[]>([]);
  const [sending, setSending] = useState(false);
  const [errors, setErrors] = useState({ emails: '', workspaces: '', role: '' });

  const { checkForRolesAccessOnMember } = useRolesResolver(roles);

  // Same role linking as the Team tab: roles whose permissions are fully covered
  // by the selected roles get auto-selected (resolved via nesting) and locked;
  // `selectedRoles` keeps only the explicitly assigned roles.
  const { allSelectedRoles, rolesActionsMap, disabledRoles } = useMemo(
    () => checkForRolesAccessOnMember(selectedRoles, null),
    [selectedRoles, checkForRolesAccessOnMember]
  );

  const optionsTooltipComponent = useCallback(
    option => optionsTooltipResolver(option, rolesActionsMap),
    [rolesActionsMap]
  );

  // Pre-select first available workspace; default role to Observer on open
  useEffect(() => {
    if (open) {
      setEmails('');
      setSelectedWorkspace(workspaces.length > 0 ? workspaces[0] : null);
      const defaultRole = roles.find(r => r.roleName === 'OBSERVER');
      setSelectedRoles(defaultRole ? [defaultRole] : []);
      setErrors({ emails: '', workspaces: '', role: '' });
      setSending(false);
    }
  }, [open, roles, workspaces]);

  const validate = () => {
    const emailList = emails
      .split(',')
      .map(e => e.trim())
      .filter(Boolean);
    const newErrors = { emails: '', workspaces: '', role: '' };
    let valid = true;

    if (!emailList.length) {
      newErrors.emails = 'At least one email address is required';
      valid = false;
    } else if (emailList.some(e => !VALID_EMAIL_REGEX.test(e))) {
      newErrors.emails = 'One or more email addresses are invalid';
      valid = false;
    }

    if (!selectedWorkspace) {
      newErrors.workspaces = 'Please select a workspace';
      valid = false;
    }

    if (!selectedRoles.length) {
      newErrors.role = 'Please select a role';
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setSending(true);
    const emailList = emails
      .split(',')
      .map(e => e.trim())
      .filter(Boolean);

    try {
      const res = await WorkspacesAPI.inviteWorkspaceMembers({
        workspace_id: selectedWorkspace.workspace_id,
        emails: emailList,
        rbac: { roles: selectedRoles.map(r => r.roleName) }
      });
      toasterService.pop(
        'success',
        undefined,
        res.success_message || `Invite sent to ${emailList.length} user(s)`
      );
      onInviteSuccess(emailList.length);
      onClose();
    } catch (err) {
      toasterService.pop(
        'error',
        undefined,
        getErrorMessageFromObj(err) || 'Failed to send invites. Please try again.'
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <div className={styles.modal}>
        {/* Header */}
        <div className='dialog-header'>
          <div className={styles.headerContent}>
            <div className={styles.headerIcon}>
              <IconUserPlus size={24} />
            </div>
            <span className={styles.headerTitle}>Invite Users to Workspace</span>
          </div>
          <IconButton onClick={onClose} dataId='invite-ws-modal-close' label='Close'>
            <Icon name='close' />
          </IconButton>
        </div>

        {/* Body */}
        <div className='dialog-body'>
          {/* Email field */}
          <div className={styles.fieldGroup}>
            <label htmlFor='invite-ws-email' className={styles.label}>
              Email Address<span className={styles.required}>*</span>
              <Icon name='information' size={1} className={styles.infoIcon} />
            </label>
            <TextField
              id='invite-ws-email'
              dataId='invite-ws-email'
              autoFocus={true}
              placeholder='user@acme.corp, user2@acme.corp'
              value={emails}
              onChange={e => {
                setEmails((e.target as HTMLInputElement).value);
                if (errors.emails) setErrors(prev => ({ ...prev, emails: '' }));
              }}
              error={!!errors.emails}
              helperText={errors.emails || 'Use comma-separated email addresses for multiple users'}
              disabled={sending}
              size='small'
            />
          </div>

          {/* Workspace field */}
          <div className={styles.fieldGroup}>
            <label htmlFor='invite-ws-workspace-select' className={styles.label}>
              Workspace<span className={styles.required}>*</span>
            </label>
            <div className={styles.noFocusBorderDropdown}>
              <HdDropDown
                id='invite-ws-workspace-select'
                dataId='invite-ws-workspace-select'
                options={workspaces}
                selected={selectedWorkspace}
                displayAccessor='name'
                valueAccessor='workspace_id'
                placeholder={workspaces.length === 0 ? 'No workspaces available' : 'Select Workspace'}
                onChangeEventHandler={selected => {
                  setSelectedWorkspace(selected);
                  if (errors.workspaces) setErrors(prev => ({ ...prev, workspaces: '' }));
                }}
                disabled={sending || workspaces.length === 0}
                error={!!errors.workspaces}
                helperText={workspaces.length === 0 ? 'No workspaces have been created yet.' : errors.workspaces}
                size='small'
                paperClasses={styles.dropdownWhitePaper}
              />
            </div>
          </div>

          {/* Role field */}
          <div className={styles.fieldGroup}>
            <label htmlFor='invite-ws-role-select' className={styles.label}>
              Workspace Role<span className={styles.required}>*</span>
            </label>
            <HdDropDown
              id='invite-ws-role-select'
              dataId='invite-ws-role-select'
              options={roles}
              selected={allSelectedRoles}
              displayAccessor='title'
              valueAccessor='roleName'
              multiple
              selectMode
              placeholder='Select Role'
              onChangeEventHandler={(selected: Role[]) => {
                setSelectedRoles((selected ?? []).filter(role => !role?.isResolvedViaNesting));
                if (errors.role) setErrors(prev => ({ ...prev, role: '' }));
              }}
              disabledOptions={disabledRoles}
              OptionTooltipComponent={optionsTooltipComponent}
              disabled={sending}
              error={!!errors.role}
              helperText={errors.role}
              size='small'
            />
          </div>
        </div>

        {/* Footer */}
        <div className='dialog-footer'>
          <Button
            className={styles.actionBtn}
            variation='outline'
            onClick={onClose}
            disabled={sending}
            dataId='invite-ws-cancel'
          >
            Cancel
          </Button>
          <Button
            className={`btn-primary ${styles.actionBtn}`}
            onClick={handleSubmit}
            showProgress={sending}
            disabled={sending || workspaces.length === 0}
            dataId='invite-ws-submit'
          >
            Send Invite
          </Button>
        </div>
      </div>
    </Modal>
  );
}
